import { useTranslation } from 'react-i18next';
import { Fragment, useState, useRef, ChangeEvent } from 'react';
import { i18n } from 'shared/i18n';
import { useGetDocument, useDocumentAreaSave, useGetDocumentTableOfContent, type EditorDocument, type PageArea } from 'entities/document';
import { useImage, useImagesInDocument } from 'entities/document';
import { useUser } from 'app/state/contexts';
import { DataImage, Dropdown, Spinner } from 'app/components';
import { LanguageSelector } from 'app/components';
import { useDelayedSave } from 'app/utils/hooks/delayed-save';
import { useGetDocumentBySuperId } from 'entities/document';

interface OverrideEntry {
  uuid: string;
  text: string | null;
  revisionId?: string;
}

interface HeadingLanguageOverrides {
  overrides: OverrideEntry[];
}

interface MenuIconValue {
  imageSuperId?: string | null;
  imageRevisionId?: string | null;
  targetHeaderUuid?: unknown;
  position?: unknown;
}

interface HeadingOptions {
  menuIcon?: MenuIconValue;
  overrides?: OverrideEntry[];
  [locale: string]: HeadingLanguageOverrides | MenuIconValue | OverrideEntry[] | undefined;
}

interface SimpleOptions {
  pageNumber?: boolean;
  headers?: boolean;
  metadataText: string[];
}

interface TocHeader {
  uuid: string;
  name: string;
  headerType?: string;
  revisionId?: string;
}

interface ImageFile {
  superId: string;
  revisionId: string;
  filename?: string;
  fileType: string;
  fileContent: string;
}

interface DocumentImage {
  file: ImageFile;
}

interface AreaSettingsProps {
  documentId: string;
}

export const AreaSettings = ({ documentId }: AreaSettingsProps) => {
  const { t } = useTranslation('project-details');

  const { selectedOrganization } = useUser();
  const { data: pDocument } = useGetDocument(documentId, selectedOrganization);

  const { mutateAsync: saveArea } = useDocumentAreaSave();

  if (!pDocument) {
    return <Spinner />;
  }

  const headerType = pDocument.content.header?.type || null;
  const footerType = pDocument.content.footer?.type || null;

  function onChangeHeaderType(type: unknown) {
    switch (type) {
      case 'headings':
        onChangeHeaderOptions(type, defaultHeadingValues);
        break;
      case 'simple':
        onChangeHeaderOptions(type, defaultSimpleValues);
        break;
      case 'combined':
        onChangeHeaderOptions(type, defaultCombinedValues);
        break;
      default:
        onChangeHeaderOptions(null);
    }
  }

  function onChangeFooterType(type: unknown) {
    switch (type) {
      case 'headings':
        onChangeFooterOptions(type, defaultHeadingValues);
        break;
      case 'simple':
        onChangeFooterOptions(type, defaultSimpleValues);
        break;
      case 'combined':
        onChangeFooterOptions(type, defaultCombinedValues);
        break;
      default:
        onChangeFooterOptions(null);
    }
  }

  function onChangeHeaderOptions(type: unknown, options?: unknown) {
    saveArea({
      documentSuperId: documentId,
      documentRevisionId: pDocument?.revisionId,
      header: type ? { type, options } : null,
    });
  }

  function onChangeFooterOptions(type: unknown, options?: unknown) {
    saveArea({
      documentSuperId: documentId,
      documentRevisionId: pDocument?.revisionId,
      footer: type ? { type, options } : null,
    });
  }

  const areaTypes = [
    { title: t('area.type.none'), value: null },
    { title: t('area.type.simple'), value: 'simple' },
    { title: t('area.type.headings'), value: 'headings' },
    { title: t('area.type.combined'), value: 'combined' },
  ];

  return (
    <div className="mb-5 pb-5">
      <h2 className="fs-1">{t('area.header')}</h2>
      <div className="w-25 mb-4">
        <Dropdown items={areaTypes} onChange={onChangeHeaderType} value={headerType} />
      </div>
      <Customization onChange={onChangeHeaderOptions} language={pDocument.content.locale} area={pDocument.content.header} documentId={documentId} />
      <h2 className="fs-1 mt-5">{t('area.footer')}</h2>
      <div className="w-25 mb-4">
        <Dropdown items={areaTypes} onChange={onChangeFooterType} value={footerType} />
      </div>
      <Customization onChange={onChangeFooterOptions} language={pDocument.content.locale} area={pDocument.content.footer} documentId={documentId} />
    </div>
  );
};

interface CustomizationProps {
  area?: PageArea | null;
  language?: string;
  documentId: string;
  onChange: (type: unknown, options?: unknown) => void;
}

const Customization = ({ area, language: _language, ...props }: CustomizationProps) => {
  const type = area?.type;
  if (type === 'headings') {
    return <HeadingArea options={(area?.options as HeadingOptions | undefined) || defaultHeadingValues} {...props} />;
  } else if (type === 'simple') {
    return <SimpleArea options={(area?.options as SimpleOptions | undefined) || defaultSimpleValues} {...props} />;
  } else if (type === 'combined') {
    return <CombinedArea options={(area?.options as CombinedOptions | undefined) || defaultCombinedValues} {...props} />;
  }

  return null;
};

interface CombinedAreaProps {
  documentId: string;
  onChange: (type: unknown, options?: unknown) => void;
  options: CombinedOptions;
}

const CombinedArea = ({ onChange, options, documentId }: CombinedAreaProps) => {
  const latest = useRef<CombinedOptions>(options);

  const saveHeadings = (_type: unknown, headingValue?: unknown) => {
    const simpleValue = Object.fromEntries(SIMPLE_OPTION_KEYS.map((key) => [key, latest.current[key]]));
    latest.current = { ...(headingValue as CombinedOptions), ...simpleValue };
    onChange('combined', latest.current);
  };

  const saveSimple = (_type: unknown, simpleValue?: unknown) => {
    const ownValue = Object.fromEntries(SIMPLE_OPTION_KEYS.map((key) => [key, (simpleValue as CombinedOptions)[key]]));
    latest.current = { ...latest.current, ...ownValue };
    onChange('combined', latest.current);
  };

  return (
    <>
      <HeadingArea options={options as HeadingOptions} onChange={saveHeadings} documentId={documentId} />
      <div className="mt-5">
        <SimpleArea options={{ ...defaultSimpleValues, ...(options as Partial<SimpleOptions>) }} onChange={saveSimple} documentId={documentId} />
      </div>
    </>
  );
};

interface MenuIconSettingsProps {
  documentId: string;
  menuIcon?: MenuIconValue;
  onChange: (menuIcon: MenuIconValue) => void;
}

const MenuIconSettings = ({ documentId, menuIcon, onChange }: MenuIconSettingsProps) => {
  const { t } = useTranslation(['area', 'imageimport']);

  const { selectedOrganization } = useUser();
  const { data: pDocument } = useGetDocument(documentId, selectedOrganization);
  const { data: imageData } = useImage(menuIcon?.imageRevisionId as string | undefined, 'thumbnail') as { data?: { file?: ImageFile } | null };
  const { data: documentImagesData, isLoading: imagesLoading } = useImagesInDocument(pDocument?.superId);
  const documentImages = documentImagesData as DocumentImage[] | undefined;
  const [showImagePicker, setShowImagePicker] = useState(false);

  const { data: tocData } = useGetDocumentTableOfContent(pDocument?.revisionId ?? null, {
    start: 'DOCUMENT_START',
    stop: 'DOCUMENT_END',
    headerHierarchy: ['h1', 'h2', 'h3'],
    locale: pDocument?.content?.locale,
  });

  if (!pDocument) {
    return <Spinner />;
  }

  const headingItems = [
    { title: t('area:menu-icon.no-target'), value: null },
    ...((tocData?.content as TocHeader[] | undefined)?.map((h) => ({ title: `${h.headerType?.toUpperCase() || 'H'} — ${h.name}`, value: h.uuid })) || []),
  ];

  const positionItems = [
    { title: t('area:menu-icon.position.left'), value: 'left' },
    { title: t('area:menu-icon.position.right'), value: 'right' },
  ];

  const handleSelectImage = (image: DocumentImage) => {
    onChange({ ...menuIcon, imageSuperId: image.file.superId, imageRevisionId: image.file.revisionId });
    setShowImagePicker(false);
  };

  const handleRemoveIcon = () => {
    onChange({ ...menuIcon, imageSuperId: null, imageRevisionId: null });
  };

  const handleTargetChange = (targetHeaderUuid: unknown) => {
    onChange({ ...menuIcon, targetHeaderUuid });
  };

  const handlePositionChange = (position: unknown) => {
    onChange({ ...menuIcon, position });
  };

  return (
    <div className="mb-4 mt-3 p-3 border rounded-3">
      <h3 className="subheading text-blue-600 mb-3">{t('area:menu-icon.title')}</h3>

      <div className="row mb-3">
        <div className="col-12">
          <label className="form-label fw-semibold">{t('area:menu-icon.image')}</label>
          {menuIcon?.imageRevisionId && imageData?.file ? (
            <div className="d-flex align-items-center gap-3 mb-2">
              <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <DataImage {...(imageData?.file as ImageFile)} style={{ maxWidth: 48, maxHeight: 48 }} />
              </div>
              <button className="btn btn-sm btn-outline-primary me-1" onClick={() => setShowImagePicker(true)}>
                <i className="fa-regular fa-pen me-1" />
                {t('area:menu-icon.change')}
              </button>
              <button className="btn btn-sm btn-outline-danger" onClick={handleRemoveIcon}>
                <i className="fa-regular fa-trash-can me-1" />
                {t('area:menu-icon.remove')}
              </button>
            </div>
          ) : (
            <div>
              <button className="btn btn-sm btn-outline-primary" onClick={() => setShowImagePicker(true)}>
                <i className="fa-regular fa-image me-1" />
                {t('area:menu-icon.select-image')}
              </button>
            </div>
          )}
          {showImagePicker && (
            <div className="mt-2 p-3 border rounded-3 bg-white" style={{ maxHeight: 300, overflowY: 'auto' }}>
              {imagesLoading ? (
                <p className="text-muted">{t('imageimport:loading')}</p>
              ) : (documentImages?.length ?? 0) > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {documentImages?.map((image) => (
                    <div
                      key={image.file.superId}
                      className={`border rounded-2 p-1 cursor-pointer ${menuIcon?.imageSuperId === image.file.superId ? 'border-primary border-2' : ''}`}
                      style={{ width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                      onClick={() => handleSelectImage(image)}
                      title={image.file.filename}
                    >
                      <DataImage {...image.file} style={{ maxWidth: 64, maxHeight: 64, objectFit: 'contain' }} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted mb-0">{t('area:menu-icon.no-images')}</p>
              )}
              <button className="btn btn-sm btn-link mt-2 p-0" onClick={() => setShowImagePicker(false)}>
                {t('area:menu-icon.cancel')}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-6">
          <label className="form-label fw-semibold">{t('area:menu-icon.target-heading')}</label>
          <Dropdown items={headingItems} onChange={handleTargetChange} value={menuIcon?.targetHeaderUuid || null} sm />
        </div>
        <div className="col-6">
          <label className="form-label fw-semibold">{t('area:menu-icon.position')}</label>
          <Dropdown items={positionItems} onChange={handlePositionChange} value={menuIcon?.position || 'left'} sm />
        </div>
      </div>
    </div>
  );
};

interface HeadingAreaProps {
  documentId: string;
  onChange: (type: unknown, options?: unknown) => void;
  options: HeadingOptions;
}

const HeadingArea = ({ documentId, onChange, options }: HeadingAreaProps) => {
  const { selectedOrganization } = useUser();
  const { data: pDocument } = useGetDocument(documentId, selectedOrganization);
  const [language, setLanguage] = useState(pDocument?.content.locale);
  const [value, setValue] = useState<HeadingOptions>(options);
  const [save] = useDelayedSave(onChange, 1000);

  const { data } = useGetDocumentTableOfContent(pDocument?.revisionId ?? null, {
    start: 'DOCUMENT_START',
    stop: 'DOCUMENT_END',
    headerHierarchy: ['h1'],
    locale: language,
  });

  if (!pDocument) {
    return <Spinner />;
  }

  function handleChange(header: TocHeader, newOverride: string | null) {
    const langSlice = value?.[language as string] as HeadingLanguageOverrides | undefined;
    const otherOverrides = langSlice?.overrides.filter((override) => override.uuid !== header.uuid) || [];
    const newValue: HeadingOptions = {
      ...value,
      [language as string]: { overrides: [...otherOverrides, { uuid: header.uuid, text: newOverride, revisionId: header.revisionId }] },
    };
    setValue(newValue);
    save('headings', newValue);
  }

  function handleMenuIconChange(menuIcon: MenuIconValue) {
    const newValue: HeadingOptions = { ...value, menuIcon };
    setValue(newValue);
    save('headings', newValue);
  }

  function getOverride(value: HeadingOptions | undefined, header: TocHeader) {
    const langSlice = value?.[language as string] as HeadingLanguageOverrides | undefined;
    return langSlice?.overrides.find((override) => override.uuid === header.uuid)?.text;
  }

  return (
    <>
      <MenuIconSettings documentId={documentId} menuIcon={value?.menuIcon} onChange={handleMenuIconChange} />
      <div className="mt-4 w-25">
        <LanguageSelector sm locale={language} onChange={setLanguage} />
      </div>
      <div className="row w-50 mt-3">
        <div className="col-4">
          <h3 className="subheading text-blue-600">{i18n('toc:hierarchy.headers')}</h3>
        </div>
        <div className="col-8">
          <h3 className="subheading text-blue-600">{i18n('toc:hierarchy.override')}</h3>
        </div>
        {(data?.content as TocHeader[] | undefined)?.map((header) => (
          <Fragment key={header.uuid}>
            <div className="col-4 d-flex align-items-center">
              <input
                className="form-check-input ms-0 me-3 mt-0 flex-shrink-0"
                checked={getOverride(value, header) !== null}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(header, e.target.checked ? '' : null)}
                type="checkbox"
                role="switch"
              />
              <label className={`col-form-label col-form-label-sm ${getOverride(value, header) === null ? 'text-decoration-line-through' : ''}`}>
                {header.name}
              </label>
            </div>
            <div className="col-7">
              <input
                className="form-control form-control-sm"
                disabled={getOverride(value, header) === null}
                type="text"
                value={getOverride(value, header) || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(header, e.target.value)}
              />
            </div>
          </Fragment>
        ))}
      </div>
    </>
  );
};

interface SimpleAreaProps {
  onChange: (type: unknown, options?: unknown) => void;
  options: SimpleOptions;
  documentId: string;
}

const SimpleArea = ({ onChange, options, documentId }: SimpleAreaProps) => {
  const { t } = useTranslation('project-details');

  const { data: pDocument } = useGetDocumentBySuperId(documentId) as { data?: EditorDocument };
  const [save] = useDelayedSave(onChange, 250);
  const [value, setValue] = useState<SimpleOptions>(options);

  if (!pDocument) {
    return <Spinner />;
  }

  const items = Object.entries((pDocument?.content?.metadata?.texts?.[pDocument.content?.locale ?? 'sv-SE'] || {}) as Record<string, string>)
    .map(([key, value]) => ({ value: key, title: value }))
    .filter((item) => item.title);

  const handleMetaTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const remove = !e.target.checked;
    const text = e.target.value;
    let newValue: SimpleOptions;
    if (remove) {
      newValue = { ...value, metadataText: value.metadataText.filter((t) => t !== text) };
    } else {
      newValue = { ...value, metadataText: [...value.metadataText, text] };
    }
    save('simple', newValue);
    setValue(newValue);
  };

  const handlePageNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue: SimpleOptions = { ...value, pageNumber: e.target.checked };
    save('simple', newValue);
    setValue(newValue);
  };

  return (
    <div>
      <div className="mb-4 form-switch row ps-0">
        <label className="col-2  d-flex align-items-center">{t('area.page-number')}</label>
        <div className="col-5">
          <input className="form-check-input ms-0" checked={value?.pageNumber} onChange={handlePageNumberChange} type="checkbox" role="switch" />
        </div>
      </div>
      {items?.length > 0 ? (
        <>
          <h3 className="fs-2">{t('area.text')}</h3>
          <div className="mb-4 row">
            {items.map((item) => (
              <div key={item.value} className="row">
                <div className="col-2">{item.title}</div>
                <div className="col-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={value?.metadataText?.includes(item.value)}
                    value={item.value}
                    onChange={handleMetaTextChange}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
};

const defaultSimpleValues: SimpleOptions = { pageNumber: false, headers: false, metadataText: [] };
const defaultHeadingValues: HeadingOptions = { overrides: [] };
type CombinedOptions = Record<string, unknown>;
const defaultCombinedValues: CombinedOptions = { ...defaultHeadingValues, ...defaultSimpleValues };
const SIMPLE_OPTION_KEYS = Object.keys(defaultSimpleValues);
